import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import styled from 'styled-components';
import {
  FiCreditCard, FiPlus, FiArrowDownCircle, FiArrowUpCircle,
  FiRefreshCw, FiClock, FiCheckCircle, FiXCircle, FiInbox, FiX, FiCopy,
  FiMessageCircle,
} from 'react-icons/fi';
import { FaDiscord } from 'react-icons/fa';

import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import { getWallet, getTransactions, createTopUp } from '../../../../store/wallet/walletSlice';

/* ── Wrappers ─────────────────────────────── */
const Page = styled.div`
  padding: 28px 24px;
  max-width: 860px;
  margin: 0 auto;
`;

const BalanceCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
  padding: 24px;
  background: linear-gradient(135deg, rgba(147,51,234,0.16), rgba(99,102,241,0.08));
  border: 1px solid rgba(147,51,234,0.25);
  border-radius: 12px;
  flex-wrap: wrap;
`;

const BalanceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .icon-box {
    width: 54px; height: 54px; border-radius: 14px; flex-shrink: 0;
    background: rgba(147,51,234,0.18); border: 1px solid rgba(147,51,234,0.3);
    display: flex; align-items: center; justify-content: center;
    svg { color: #a78bfa; font-size: 24px; }
  }
  .label { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
  .amount { font-size: 28px; font-weight: 800; color: #f1f5f9; direction: ltr; display: inline-block; }
  .currency { font-size: 14px; color: #a78bfa; font-weight: 600; margin-inline-start: 6px; }
`;

const BalanceActions = styled.div`
  display: flex; align-items: center; gap: 8px; flex-shrink: 0;
`;

const RefreshBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; border-radius: 9px;
  background: transparent; border: 1px solid rgba(255,255,255,0.12);
  color: #9ca3af; cursor: pointer; transition: all 0.15s;
  svg { font-size: 15px; ${p => p.spinning && 'animation: spin 0.8s linear infinite;'} }
  &:hover { border-color: rgba(147,51,234,0.4); color: #c4b5fd; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

const TopUpBtn = styled.button`
  display: flex; align-items: center; gap: 7px;
  padding: 10px 20px; border-radius: 9px; border: none;
  background: #7C3AED; color: #fff;
  font-family: inherit; font-size: 13.5px; font-weight: 600;
  cursor: pointer; transition: opacity 0.15s; flex-shrink: 0;
  &:hover { opacity: 0.85; }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  align-items: center;
  gap: 12px;

  .icon {
    width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: ${p => p.tint || 'rgba(147,51,234,0.1)'};
    border: 1px solid ${p => p.border || 'rgba(147,51,234,0.2)'};
    svg { color: ${p => p.color || '#a78bfa'}; font-size: 16px; }
  }
  .label { font-size: 11px; color: #6b7280; margin-bottom: 3px; }
  .value { font-size: 16px; font-weight: 700; color: #e2e8f0; direction: ltr; display: inline-block; }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 14.5px;
  font-weight: 700;
  color: #e2e8f0;
  svg { color: #a78bfa; font-size: 16px; }
`;

const Card = styled.div`
  background: #1A1A1A;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  overflow: hidden;
`;

const TxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  &:last-child { border-bottom: none; }

  .tx-icon {
    width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: ${p => p.tint};
    border: 1px solid ${p => p.border};
    svg { color: ${p => p.iconColor}; font-size: 17px; }
  }

  .tx-info { flex: 1; min-width: 0; }
  .tx-title { font-size: 13.5px; font-weight: 600; color: #e2e8f0; }
  .tx-date { font-size: 11.5px; color: #6b7280; margin-top: 2px; direction: ltr; text-align: right; }

  .tx-right { text-align: right; flex-shrink: 0; }
  .tx-amount { font-size: 14px; font-weight: 700; direction: ltr; display: inline-block; color: ${p => p.amountColor}; }

  @media (max-width: 480px) {
    .tx-date { text-align: inherit; }
  }
`;

const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10.5px; font-weight: 700; padding: 2px 9px; border-radius: 20px;
  margin-top: 4px;
  background: ${p => p.bg}; color: ${p => p.color}; border: 1px solid ${p => p.border};
  svg { font-size: 10px; }
`;

const EmptyBox = styled.div`
  text-align: center; padding: 48px 24px; color: #4b5563;
  svg { font-size: 36px; margin-bottom: 12px; opacity: 0.4; }
  .t { font-size: 15px; font-weight: 600; color: #6b7280; }
  .s { font-size: 13px; margin-top: 6px; }
`;

/* ── Top Up Modal ─────────────────────────── */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: #161616;
  border: 1px solid rgba(147, 51, 234, 0.25);
  border-radius: 14px;
  padding: 24px;
  max-width: 400px;
  width: 100%;
  position: relative;

  h3 { font-size: 16px; font-weight: 800; color: #f1f5f9; margin: 0 0 4px; }
  p { font-size: 12px; color: #6b7280; margin: 0 0 18px; }

  .close-btn {
    position: absolute; top: 16px; inset-inline-end: 16px;
    background: none; border: none; color: #6b7280; cursor: pointer;
    font-size: 16px; display: flex;
    &:hover { color: #e2e8f0; }
  }
`;

const FieldLabel = styled.div`
  font-size: 12px; font-weight: 600; color: #9ca3af;
  margin-bottom: 9px; text-transform: uppercase; letter-spacing: 0.04em;
`;

const DiscordButton = styled.a`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 11px 14px; border-radius: 9px; margin-bottom: 18px;
  background: rgba(88,101,242,0.12); border: 1px solid rgba(88,101,242,0.4);
  color: #a3aeff; font-size: 14px; font-weight: 700; text-decoration: none;
  transition: opacity 0.15s;
  svg { font-size: 18px; }
  &:hover { opacity: 0.85; }
`;

const AmountBox = styled.div`
  position: relative;
  margin-bottom: 8px;

  .prefix {
    position: absolute; inset-inline-start: 14px; top: 50%; transform: translateY(-50%);
    color: #6b7280; font-size: 14px; font-weight: 700;
  }

  input {
    width: 100%; background: #111; border: 1px solid rgba(255,255,255,0.08);
    border-radius: 9px; padding: 11px 14px 11px 30px; color: #e2e8f0; font-size: 14px;
    font-family: inherit; outline: none; direction: ltr; text-align: left;
    transition: border-color 0.2s;
    &::placeholder { color: #374151; }
    &:focus { border-color: rgba(147,51,234,0.5); background: #151515; }
  }
`;

const QuickAmounts = styled.div`
  display: flex; gap: 7px; margin-bottom: 18px;

  button {
    flex: 1; padding: 7px 0; border-radius: 7px; cursor: pointer;
    background: #111; border: 1px solid rgba(255,255,255,0.08);
    color: #9ca3af; font-size: 12px; font-weight: 600; font-family: inherit;
    transition: all 0.15s;
    &:hover { border-color: rgba(147,51,234,0.4); color: #c4b5fd; }
  }
`;

const FieldError = styled.div`
  font-size: 12px; color: #f87171; margin-bottom: 14px;
`;

const ModalActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;

  button {
    padding: 9px 18px; border-radius: 9px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit; border: none; transition: opacity 0.15s;
  }
  .cancel-btn { background: transparent; color: #9ca3af; border: 1px solid rgba(255,255,255,0.1); }
  .cancel-btn:hover { border-color: rgba(255,255,255,0.2); color: #e2e8f0; }
  .submit-btn { background: #7C3AED; color: #fff; }
  .submit-btn:hover { opacity: 0.85; }
  .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PaymentBox = styled.div`
  background: #111; border: 1px solid rgba(255,255,255,0.08); border-radius: 10px;
  padding: 14px; margin-bottom: 16px;

  .row { margin-bottom: 12px; &:last-child { margin-bottom: 0; } }
  .row-label { font-size: 11px; color: #6b7280; margin-bottom: 5px; }
  .row-value {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 600; color: #e2e8f0; word-break: break-all; direction: ltr;
  }
  .copy-btn {
    flex-shrink: 0; display: flex; align-items: center; gap: 4px;
    background: rgba(147,51,234,0.12); border: 1px solid rgba(147,51,234,0.3);
    color: #c4b5fd; border-radius: 6px; padding: 4px 8px; font-size: 11px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    &:hover { background: rgba(147,51,234,0.2); }
  }
`;

const WaitingNote = styled.div`
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 12px; color: #fbbf24; margin-bottom: 18px;
  background: rgba(251,191,36,0.08); border: 1px solid rgba(251,191,36,0.25);
  border-radius: 8px; padding: 10px 12px;
  svg { flex-shrink: 0; margin-top: 1px; }
`;

const Toast = styled.div`
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  background: #166534; border: 1px solid #15803d; border-radius: 10px;
  padding: 12px 24px; color: #bbf7d0; font-size: 13.5px; font-weight: 600;
  z-index: 9999; box-shadow: 0 4px 20px rgba(0,0,0,0.4);
`;

const TYPE_META = {
  deposit: { icon: <FiArrowDownCircle />, tint: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.25)', color: '#34d399' },
  purchase: { icon: <FiArrowUpCircle />, tint: 'rgba(248,113,113,0.1)', border: 'rgba(248,113,113,0.25)', color: '#f87171' },
  refund: { icon: <FiRefreshCw />, tint: 'rgba(147,51,234,0.1)', border: 'rgba(147,51,234,0.25)', color: '#a78bfa' },
};

const STATUS_META = {
  completed: { icon: <FiCheckCircle />, bg: 'rgba(52,211,153,0.1)', color: '#34d399', border: 'rgba(52,211,153,0.25)' },
  pending: { icon: <FiClock />, bg: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: 'rgba(251,191,36,0.25)' },
  failed: { icon: <FiXCircle />, bg: 'rgba(248,113,113,0.1)', color: '#f87171', border: 'rgba(248,113,113,0.25)' },
};

const QUICK_AMOUNTS = [15, 25, 50, 100];

const txTitle = (t, tx) => tx.type === 'deposit'
  ? `${t('website.wallet.top_up')} — ${t(`website.wallet.method_${tx.method || 'usdt'}`)}`
  : t(`website.wallet.type_${tx.type}`);

const formatDate = (value) => value ? value.slice(0, 16).replace('T', ' ') : '';

/* ── Component ────────────────────────────── */
const Wallet = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const toastChakra = useToast({ position: 'top', duration: 3000 });
  const auth = useSelector(state => state.auth);
  const wallet = useSelector(state => state.wallet);
  const settings = useSelector(state => state.settings);
  const discordInvite = settings.data?.discord;
  const binanceId = settings.data?.binance_id;

  const [modalOpen, setModalOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState('');
  const [topUpRequest, setTopUpRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (auth.isAuth) {
      dispatch(getWallet());
      dispatch(getTransactions());
    }
  }, [dispatch, auth.isAuth]);

  useEffect(() => {
    const prefillAmount = location.state?.prefillAmount;
    if (prefillAmount > 0) {
      setAmount(String(prefillAmount));
      setModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!auth.isAuth) return <Navigate to="/login" />;

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([dispatch(getWallet()), dispatch(getTransactions())]);
    setRefreshing(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setAmount('');
    setFormError('');
    setTopUpRequest(null);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleCopy = (value) => {
    navigator.clipboard.writeText(value);
    toastChakra({ description: t('website.wallet.copied'), status: 'success' });
  };

  const handleTopUpSubmit = () => {
    const value = Number(amount);
    if (!amount || Number.isNaN(value) || value <= 0) {
      setFormError(t('website.wallet.valid_amount_error'));
      return;
    }
    setFormError('');
    setSubmitting(true);

    dispatch(createTopUp({ amount: value }))
      .unwrap()
      .then((res) => {
        setTopUpRequest(res.data);
        dispatch(getTransactions());
      })
      .catch((err) => {
        toastChakra({ description: err?.errors?.[0]?.msg || 'something went wrong', status: 'error' });
      })
      .finally(() => setSubmitting(false));
  };

  const handleDone = () => {
    closeModal();
    refresh();
    showToast(t('website.wallet.top_up_success'));
  };

  const transactions = wallet.transactions || [];

  return (
    <WebsiteLayout>
      <Page>
        <BalanceCard>
          <BalanceInfo>
            <div className="icon-box"><FiCreditCard /></div>
            <div>
              <div className="label">{t('website.wallet.balance')}</div>
              <span className="amount">{wallet.balance}</span>
              <span className="currency">{t('website.wallet.currency')}</span>
            </div>
          </BalanceInfo>
          <BalanceActions>
            <RefreshBtn type="button" spinning={refreshing} onClick={refresh} aria-label={t('website.wallet.refresh')}>
              <FiRefreshCw />
            </RefreshBtn>
            <TopUpBtn type="button" onClick={() => setModalOpen(true)}>
              <FiPlus /> {t('website.wallet.top_up')}
            </TopUpBtn>
          </BalanceActions>
        </BalanceCard>

        <StatsRow>
          <StatBox tint="rgba(52,211,153,0.1)" border="rgba(52,211,153,0.25)" color="#34d399">
            <div className="icon"><FiArrowDownCircle /></div>
            <div>
              <div className="label">{t('website.wallet.total_deposits')}</div>
              <span className="value">+{wallet.totalDeposits} {t('website.wallet.currency')}</span>
            </div>
          </StatBox>
          <StatBox tint="rgba(248,113,113,0.1)" border="rgba(248,113,113,0.25)" color="#f87171">
            <div className="icon"><FiArrowUpCircle /></div>
            <div>
              <div className="label">{t('website.wallet.total_spent')}</div>
              <span className="value">-{wallet.totalSpent} {t('website.wallet.currency')}</span>
            </div>
          </StatBox>
          <StatBox tint="rgba(147,51,234,0.1)" border="rgba(147,51,234,0.25)" color="#a78bfa">
            <div className="icon"><FiCreditCard /></div>
            <div>
              <div className="label">{t('website.wallet.transactions_count')}</div>
              <span className="value">{wallet.transactionsCount}</span>
            </div>
          </StatBox>
        </StatsRow>

        <SectionTitle>
          <FiClock /> {t('website.wallet.recent_transactions')}
        </SectionTitle>

        <Card>
          {transactions.length === 0 ? (
            <EmptyBox>
              <FiInbox />
              <div className="t">{t('website.wallet.no_transactions')}</div>
              <div className="s">{t('website.wallet.no_transactions_sub')}</div>
            </EmptyBox>
          ) : (
            transactions.map(tx => {
              const type = TYPE_META[tx.type];
              const status = STATUS_META[tx.status];
              const positive = tx.amount > 0;
              return (
                <TxRow
                  key={tx._id}
                  tint={type.tint}
                  border={type.border}
                  iconColor={type.color}
                  amountColor={positive ? '#34d399' : '#f87171'}
                >
                  <div className="tx-icon">{type.icon}</div>
                  <div className="tx-info">
                    <div className="tx-title">{txTitle(t, tx)}</div>
                    <StatusBadge bg={status.bg} color={status.color} border={status.border}>
                      {status.icon} {t(`website.wallet.status_${tx.status}`)}
                    </StatusBadge>
                  </div>
                  <div className="tx-right">
                    <div className="tx-amount">{positive ? '+' : ''}{tx.amount} {t('website.wallet.currency')}</div>
                    <div className="tx-date">{formatDate(tx.created_at)}</div>
                  </div>
                </TxRow>
              );
            })
          )}
        </Card>
      </Page>

      {modalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={closeModal} aria-label="close"><FiX /></button>
            <h3>{t('website.wallet.top_up_modal_title')}</h3>
            <p>{t('website.wallet.top_up_modal_sub')}</p>

            {!topUpRequest ? (
              <>
                <FieldLabel>{t('website.wallet.amount_label')}</FieldLabel>
                <AmountBox>
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="1"
                    placeholder={t('website.wallet.amount_placeholder')}
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                  />
                </AmountBox>
                <QuickAmounts>
                  {QUICK_AMOUNTS.map(v => (
                    <button type="button" key={v} onClick={() => setAmount(String(v))}>${v}</button>
                  ))}
                </QuickAmounts>

                {formError && <FieldError>{formError}</FieldError>}

                <ModalActions>
                  <button type="button" className="cancel-btn" onClick={closeModal}>
                    {t('website.wallet.cancel')}
                  </button>
                  <button type="button" className="submit-btn" disabled={submitting} onClick={handleTopUpSubmit}>
                    {t('website.wallet.confirm_top_up')}
                  </button>
                </ModalActions>
              </>
            ) : (
              <>
                <PaymentBox>
                  <div className="row">
                    <div className="row-label">{t('website.wallet.amount_label')}</div>
                    <div className="row-value">
                      <span>{topUpRequest.amount} {t('website.wallet.currency')}</span>
                    </div>
                  </div>
                  {binanceId && (
                    <div className="row">
                      <div className="row-label" style={{ color: '#f87171', fontWeight: 700 }}>
                        {t('website.wallet.binance_id_label')}
                      </div>
                      <div className="row-value" style={{ color: '#f87171' }}>
                        <span>{binanceId}</span>
                        <button type="button" className="copy-btn" onClick={() => handleCopy(binanceId)}>
                          <FiCopy /> {t('website.wallet.copy')}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="row">
                    <div className="row-label">{t('website.wallet.request_reference')}</div>
                    <div className="row-value">
                      <span>{topUpRequest._id}</span>
                      <button type="button" className="copy-btn" onClick={() => handleCopy(topUpRequest._id)}>
                        <FiCopy /> {t('website.wallet.copy')}
                      </button>
                    </div>
                  </div>
                </PaymentBox>

                <DiscordButton href={discordInvite || '#'} target="_blank" rel="noreferrer">
                  <FaDiscord /> {t('website.wallet.open_discord_ticket')}
                </DiscordButton>

                <WaitingNote>
                  <FiMessageCircle />
                  <div>
                    <strong>{t('website.wallet.waiting_payment')}</strong><br />
                    {t('website.wallet.waiting_payment_sub')}
                  </div>
                </WaitingNote>

                <ModalActions>
                  <button type="button" className="submit-btn" onClick={handleDone}>
                    {t('website.wallet.done')}
                  </button>
                </ModalActions>
              </>
            )}
          </ModalBox>
        </ModalOverlay>
      )}

      {toast && <Toast>{toast}</Toast>}
    </WebsiteLayout>
  );
};

export default Wallet;
